import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useMySubscription } from "./useMySubscription";

/**
 * 🔴 CO-24 — THE SINGLE CLIENT-SIDE MIRROR OF THE SERVER'S RESTRICTED-WRITE GATE.
 *
 * The defect this closes: a tenant in `Restricted`/`Dormant` was shown fully enabled primary write
 * CTAs ("Apply for Leave") directly beneath copy saying editing is paused. The user could open the
 * form, fill it in completely, and only discover the block at submit — as a bare 403 toast.
 *
 * ⚠️ THIS HOOK IS AN AFFORDANCE, NOT A SECURITY BOUNDARY. The authority is
 * `TierEnforcementMiddleware.RestrictedWriteAllowlist` on the server, which still refuses the write
 * regardless of what the client renders. Never "simplify" a server guard away because this exists.
 *
 * ⚠️ IT MUST TRACK THAT ALLOWLIST, WHICH CHANGED ON 2026-08-07 (CO-39). Before CO-39 every Core-HR
 * write silently SUCCEEDED while restricted; now all of it 403s. A gate written against the old
 * behaviour is wrong in both directions — it would grey out buttons the server honours AND leave
 * enabled the ones it refuses. The reason the allowlist is encoded here as a small closed set of
 * *areas* rather than sprinkled per-view booleans is precisely so there is ONE place to re-derive
 * when the server list moves again.
 *
 * ── The areas, derived category-by-category from `RestrictedWriteAllowlist` ──────────────────────
 *   "auth"          /connect · /api/MobileAuth · /api/Account/{Password,ResetUserPassword,
 *                   ChangeLocale,UpdateFcmToken,UpdateWebPushToken,RegisterPushDevice,
 *                   UnregisterPushDevice,LoginLog}
 *   "billing"       /api/Billing · /api/BillingCatalog · /api/PlatformBilling · /api/Subscription
 *   "terms"         /api/PlatformTerms · /api/TermsAndPolicies
 *   "notifications" /api/Notification · /api/NotificationPreference
 *   "support"       /api/Account/Log · /api/Account/AddLogAttachment
 *   "export"        every export/download surface — BR-29, never blocked at any access state
 *                   (incl. /api/UserPayslip/BulkDownloadPayslips, /api/Storage)
 *   "read"          read-shaped POSTs, e.g. /api/Skill/GetCompanySkillsInventory
 *
 * EVERYTHING ELSE IS GATED — that is deliberately the default, so a write surface added next year
 * is correct-by-omission rather than silently enabled. In particular ALL of Core HR (employees, org
 * structure, users, onboarding/offboarding, assets, bulk upload, AI, approvals, lifecycle) and all
 * of the feature modules (attendance, leaves, payroll, impact, tournament, projects) are gated.
 *
 * ── Usage ───────────────────────────────────────────────────────────────────────────────────────
 *   const write = useWriteAccess();                 // gated surface (the common case)
 *   <Button {...write.buttonProps}>Save</Button>    // disabled + a reason the user can act on
 *
 *   const write = useWriteAccess("export");         // never blocked
 *   if (write.blocked) return <RestrictedNotice />; // inline explanation above a form
 *
 * 🔴 `buttonProps` DOES NOT SHIP A `title` — AND THAT IS DELIBERATE, DO NOT "RESTORE" IT.
 * The first cut of this hook put the reason in `title`. It was INERT: `buttonVariants` in
 * `components/ui/Button.jsx` sets `disabled:pointer-events-none`, so a disabled Button never
 * receives hover and its tooltip can never render. Worse, on the clocking surface the enabled-state
 * wrapper `<div title="Clock in to start your shift">` showed *its* tooltip instead, actively
 * misleading the user. A `title` is also hover-only, i.e. invisible to touch and to keyboard.
 * The reason is therefore delivered by something that survives a disabled control:
 *   - `<RestrictedWriteNotice />` — the visible, `role="status"` explanation at the top of the
 *     surface. It renders the reason with `id={RESTRICTED_WRITE_REASON_ID}`.
 *   - `<RestrictedWriteHint />` — visible inline text beside a control group that sits far from
 *     the page-level notice (long tables, floating action rows).
 *   - `buttonProps["aria-describedby"]` points a screen reader at that same text, so the control
 *     and its reason are associated without depending on hover.
 */

/**
 * DOM id of the single visible restricted-write explanation on a page. `buttonProps` references it
 * via `aria-describedby`; `RestrictedWriteNotice` renders it. Mount at most one notice per page.
 */
export const RESTRICTED_WRITE_REASON_ID = "restricted-write-reason";
export type WriteArea =
  | "auth"
  | "billing"
  | "terms"
  | "notifications"
  | "support"
  | "export"
  | "read"
  /** Anything the server will refuse while Restricted/Dormant. The default. */
  | "gated";

/** Areas the server's RestrictedWriteAllowlist (or BR-29 export carve-out) still permits. */
const ALLOWED_AREAS: ReadonlySet<WriteArea> = new Set<WriteArea>([
  "auth",
  "billing",
  "terms",
  "notifications",
  "support",
  "export",
  "read",
]);

export interface UseWriteAccessResult {
  /** True when this write would be refused by the server for billing reasons. */
  blocked: boolean;
  /** Human, actionable explanation. Null when not blocked. */
  reason: string | null;
  /**
   * Spread onto a button/menu-item to disable it AND associate it with the visible reason.
   * No `title` — see the class note above; a disabled control cannot render one.
   */
  buttonProps: {
    disabled: boolean;
    "aria-disabled": boolean | undefined;
    "aria-describedby": string | undefined;
  };
  /**
   * Tailwind classes a RAW `<button>` needs so a disabled state is visible. `components/ui/Button`
   * already carries `disabled:opacity-50`; hand-rolled buttons carry nothing, which produced
   * silently dead Edit/Delete icons on Departments & Locations. Append it to the element's own
   * className — never spread it through `buttonProps`, because callers put `{...buttonProps}`
   * before `className=` and the caller's value would win.
   */
  disabledClassName: string;
  /** Raw ladder state, for callers that need to distinguish Dormant. */
  accessState: ReturnType<typeof useMySubscription>["accessState"];
  isDormant: boolean;
}

export function useWriteAccess(area: WriteArea = "gated"): UseWriteAccessResult {
  const { t } = useTranslation();
  const { isWriteRestricted, accessState } = useMySubscription();

  const blocked = isWriteRestricted && !ALLOWED_AREAS.has(area);
  const reason = blocked
    ? t("Editing is paused until your subscription is brought up to date. Your data is safe and nothing has been deleted — ask your HR or admin to settle the account to restore editing.")
    : null;

  // Stable identity — callers put `buttonProps` in useMemo/useCallback dependency arrays, and a
  // fresh object every render would defeat every one of those memos.
  return useMemo(
    () => ({
      blocked,
      reason,
      buttonProps: {
        disabled: blocked,
        "aria-disabled": blocked ? true : undefined,
        "aria-describedby": blocked ? RESTRICTED_WRITE_REASON_ID : undefined,
      },
      disabledClassName: blocked
        ? "disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
        : "",
      accessState,
      isDormant: accessState === "Dormant",
    }),
    [blocked, reason, accessState],
  );
}

export default useWriteAccess;
